import React from 'react';
import DoubleTransitDasha from './DoubleTransitDasha';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateDisplay';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Select,
  Stack,
  Switch,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedFormControlLabel as FormControlLabel, LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem, LocalizedTextField as TextField } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';

import { STUDIO_API_BASE_URL } from '../../api/studioClient';
const EVENTS = [
  ['marriage', 'Marriage'], ['childbirth', 'Childbirth / Progeny'],
  ['job', 'New Job / Employment'], ['promotion', 'Promotion / Career Rise'],
  ['business', 'Business Start / Expansion'], ['higherEducation', 'Higher Education'],
  ['property', 'Property Purchase / Construction'], ['vehicle', 'Vehicle Purchase'],
  ['foreignTravel', 'Foreign Travel'], ['foreignSettlement', 'Foreign Settlement'],
  ['custom', 'Custom house activation'],
] as const;
const parseHouses = (value: string) => Array.from(new Set(value.split(/[,\s]+/).map(Number).filter((house) => house >= 1 && house <= 12)));
const activationLabel = (reference: any) => reference.changed ? 'Changes' : reference.activationType === 'strict' ? 'Strict' : reference.activationType === 'network' ? 'Supporting' : 'Inactive';

const ymd = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const endForHorizon = (from: string, years: number) => {
  if (!from) return '';
  const date = new Date(`${from}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setFullYear(date.getFullYear() + years);
  return ymd(date);
};
const dashaLabel = (window: any) => window.dashaSupport === 'partial' ? 'Dasha support in part' : window.dashaSupported ? 'Dasha supported' : window.dashaSupport === 'none' ? 'No Dasha support' : 'Dasha not evaluated';
const allSelectedChartsSupport = (window: any) => {
  const references = window.referenceActivations;
  return Array.isArray(references) && references.length > 1
    && references.some((ref: any) => ref.reference === 'lagna' || ref.label === 'D1')
    && references.every((ref: any) => !ref.changed && ['strict', 'network'].includes(ref.activationType));
};

type Props = {
  chartId?: string | null;
  chartData?: any;
  token?: string | null;
};

export default function DoubleTransitPanel({ chartId, chartData, token }: Props) {
  const today = React.useMemo(() => new Date(), []);
  const [event, setEvent] = React.useState('marriage');
  const [customLabel, setCustomLabel] = React.useState('Custom event');
  const [primaryHouses, setPrimaryHouses] = React.useState('');
  const [secondaryHouses, setSecondaryHouses] = React.useState('');
  const [mode, setMode] = React.useState<'strict' | 'network'>('strict');
  const [from, setFrom] = React.useState(ymd(today));
  const [horizonYears, setHorizonYears] = React.useState<number | null>(1);
  const [to, setTo] = React.useState(() => endForHorizon(ymd(today), 1));
  const [useMoonLagna, setUseMoonLagna] = React.useState(true);
  const [useSunLagna, setUseSunLagna] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<any>(null);

  const [dashaWindow, setDashaWindow] = React.useState<any>(null);

  const changeFrom = (value: string) => {
    setFrom(value);
    if (horizonYears !== null) setTo(endForHorizon(value, horizonYears));
  };
  const changeTo = (value: string) => {
    setTo(value);
    setHorizonYears([1, 5, 10, 15].find((years) => value === endForHorizon(from, years)) ?? null);
  };
  const changeHorizon = (value: string) => {
    if (value === 'custom') {
      setHorizonYears(null);
      return;
    }
    const years = Number(value);
    setHorizonYears(years);
    setTo(endForHorizon(from, years));
  };

  const calculate = async () => {
    if (!chartId && !chartData?.planets) {
      setError('Select or calculate a chart first.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${STUDIO_API_BASE_URL}/transits/double`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          ...(chartId ? { chartId } : { chartData }),
          event, from, to, mode, useMoonLagna, useVarga: false, useSunLagna,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(event === 'custom' ? { customEvent: { label: customLabel, primaryHouses: parseHouses(primaryHouses), secondaryHouses: parseHouses(secondaryHouses) } } : {}),
          dashaMode: 'parashari',
        }),
      });
      const json = await response.json();
      if (!response.ok || json.success === false) {
        throw new Error(json.message || 'Double transit calculation failed');
      }
      setResult(json.data);
      setDashaWindow(null);
    } catch (calculationError: any) {
      setError(calculationError.message || 'Double transit calculation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={800}>Guru–Shani Double Transit</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75, mb: 2 }}>
            Find periods when Jupiter and Saturn jointly activate the selected life-event factors.
            These are activation windows, not guaranteed events.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Life event</InputLabel>
              <Select value={event} label="Life event" onChange={(e) => setEvent(e.target.value)}>
                {EVENTS.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Activation mode</InputLabel>
              <Select value={mode} label="Activation mode" onChange={(e) => setMode(e.target.value as 'strict' | 'network')}>
                <MenuItem value="strict">Strict Double Transit</MenuItem>
                <MenuItem value="network">Supporting Double Activation</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="double-transit-horizon-label">Search horizon</InputLabel>
              <Select labelId="double-transit-horizon-label" value={horizonYears === null ? 'custom' : String(horizonYears)} label="Search horizon" onChange={(e) => changeHorizon(String(e.target.value))}>
                {[1, 5, 10, 15].map((years) => <MenuItem key={years} value={String(years)}>{years} {years === 1 ? 'year' : 'years'}</MenuItem>)}
                <MenuItem value="custom">Custom dates</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth type="date" label="From" value={from} onChange={(e) => changeFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField fullWidth type="date" label="To" value={to} onChange={(e) => changeTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{mode === 'strict' ? 'Strict: Jupiter and Saturn must both activate the same primary house or the same natal house lord.' : 'Supporting: Jupiter and Saturn may activate different primary houses or their natal lords for the same event. Same-target matches also qualify.'}</Typography>
          {event === 'custom' ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <TextField fullWidth label="Event label" value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} />
              <TextField fullWidth label="Primary houses (required)" placeholder="e.g. 7, 10" value={primaryHouses} onChange={(e) => setPrimaryHouses(e.target.value)} />
              <TextField fullWidth label="Supporting houses" placeholder="e.g. 2, 11" value={secondaryHouses} onChange={(e) => setSecondaryHouses(e.target.value)} />
            </Stack>
          ) : null}
          <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 2 }}>
            <FormControlLabel control={<Switch checked disabled />} label="Lagna (D1)" />
            <FormControlLabel control={<Switch checked={useMoonLagna} onChange={(e) => setUseMoonLagna(e.target.checked)} />} label="Chandra Lagna" />
            <FormControlLabel control={<Switch checked={useSunLagna} onChange={(e) => setUseSunLagna(e.target.checked)} />} label="Surya Lagna" />
          </Stack>
          <Button variant="contained" disabled={loading} onClick={calculate} sx={{ mt: 1.5 }}>
            {loading ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Calculating…</> : 'Find activation windows'}
          </Button>
        </CardContent>
      </Card>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {result && result.count === 0 ? (
        <Alert severity="info">No matching windows were found in this range. Try Supporting Double Activation or a wider range.</Alert>
      ) : null}
      {result?.windows?.length > 0 ? <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={800}>Event: {result.windows[0].eventLabel || EVENTS.find(([key]) => key === result.event)?.[1]}</Typography>
        <Typography variant="body2" color="text.secondary">{result.windows.length} qualifying transit periods. Each sign change starts a new row.</Typography>
        <Typography variant="caption" color="text.secondary">Dates in {result.timeZone || 'UTC'}. The sign-change date starts the new row; exact transition times are in Details.</Typography>
        <Typography variant="caption" color="text.secondary" display="block">Green highlights periods supported by every selected chart (Strict or Supporting). Grade reflects weighted transit factors and Dasha support.</Typography>
      </Box> : null}
      <Stack spacing={1}>
        {(result?.windows || []).map((window: any, index: number) => (
          <Card key={`${window.startISO}-${window.endISO}-${index}`} variant="outlined" sx={{ borderRadius: 3, ...(allSelectedChartsSupport(window) ? { borderColor: 'success.main', borderWidth: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.14)' : "var(--app-surface-neutral, #f0fdf4)" } : {}) }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'minmax(230px, 1.5fr) 1fr 1fr auto auto' }, gap: 1, alignItems: 'center' }}>
                <Typography fontWeight={800}>{formatDisplayDate(window.displayStartISO || window.startISO)} – {formatDisplayDate(window.displayEndISO || window.endISO)}</Typography>
                <Typography>Jupiter in {window.jupiter?.signName || '—'}</Typography>
                <Typography>Saturn in {window.saturn?.signName || '—'}</Typography>
                <Button size="small" sx={{ justifySelf: 'start' }} onClick={() => setDashaWindow(window)}>{dashaLabel(window)} · View Dasha</Button>
                <Chip sx={{ justifySelf: 'start' }} color={window.grade === 'A' ? 'success' : window.grade === 'B' ? 'warning' : 'default'} label={`${window.scoreMin < window.scoreMax ? 'Peak grade' : 'Grade'} ${window.grade} · ${window.scoreMin < window.scoreMax ? `${window.scoreMin}–${window.scoreMax}` : window.score}`} />
              </Box>
              <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{window.activationType === 'strict' ? 'Strict Double Transit' : 'Supporting Double Activation'} · {(window.referenceActivations || []).map((ref: any) => `${ref.label}: ${activationLabel(ref)}`).join(' · ')}</Typography>
                {allSelectedChartsSupport(window) ? <Chip size="small" color="success" label="All selected charts support" /> : null}
              </Stack>
              <Box component="details" aria-label="Details" sx={{ mt: 1 }}><Box component="summary" sx={{ cursor: 'pointer', fontWeight: 700 }}>Details</Box>
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                  {window.start && window.end ? <Typography variant="body2">Exact interval: {formatDisplayDateTime(window.start, { timeZone: window.timeZone })} – {formatDisplayDateTime(window.end, { timeZone: window.timeZone })} (end exclusive)</Typography> : null}
                  <Typography variant="caption" color="text.secondary">Grade thresholds: A: 70+, B: 45–69, C: below 45.</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{window.comments || window.note}</Typography>
                  {window.reasons?.length ? <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{window.reasons.map((reason: string, reasonIndex: number) => <Typography component="li" variant="body2" key={reasonIndex} sx={{ whiteSpace: 'pre-line', mb: 1 }}>{reason}</Typography>)}</Box> : null}
                  {window.phases?.length ? <Box>
                    <Typography fontWeight={700} variant="body2">Motion and Dasha timeline</Typography>
                    <Typography variant="caption" color="text.secondary">Each interval ends when the next starts.</Typography>
                    {window.phases.map((phase: any, phaseIndex: number) => <Box key={phaseIndex} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" fontWeight={700}>{formatDisplayDateTime(phase.start || phase.startISO, { timeZone: window.timeZone })} – {formatDisplayDateTime(phase.end || phase.endISO, { timeZone: window.timeZone })}</Typography>
                      <Typography variant="body2">Jupiter: {phase.jupiter?.motion} · Saturn: {phase.saturn?.motion}</Typography>
                      <Typography variant="body2" color="text.secondary">{phase.dasha?.join(' · ') || 'Dasha unavailable'}{phase.dashaEvaluated ? phase.dashaSupported ? ' · Supports event' : ' · No event support' : ''}</Typography>
                    </Box>)}
                  </Box> : null}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
      {result?.windows?.length > 0 ? <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>These periods indicate astrological activation, not guaranteed events.</Typography> : null}
      {dashaWindow && <DoubleTransitDasha chart={chartData} window={dashaWindow} onClose={() => setDashaWindow(null)} />}
    </Box>
  );
}
